import { t } from "i18next"
import { defer, filter, map, Observable, reduce, retry, tap } from "rxjs"

import {
  type EquityError,
  EquityErrorType,
  ERROR_HISTORICAL_BAR_RESULT,
  ERROR_LATEST_BAR_RESULT,
  ERROR_QUOTE_RESULT,
} from "@/generated/TradingGateway"
import { withConnection } from "@/services/withConnection"

import { clearEquitiesError, publishEquitiesError } from "../errorAlert"
import { equityErrorKey } from "../errorAlert/equityErrorTranslationKey"
import {
  type BarResponse,
  type EquityQuoteRequest,
  type EquityQuoteResponse,
  type HistoricalBarRequest,
  type HistoricalBarResult,
  type LatestBarRequest,
  type LatestBarResult,
  MarketDataService,
  type QuoteResult,
  type SuccessLatestBarResult,
  type SuccessQuoteResult,
} from "../TradingGateway"

type MarketDataFunction = "latestBars" | "historicalBars" | "latestQuote"

type HistoricalBarsResponse = {
  bars: BarResponse[]
  error: EquityError | null
}

/**
 * @README
 * The three market data streams are independent but share the single
 * `marketData` error source, so a success on one must not clear another's
 * error. Errors are held per dataset here and only the aggregate reaches the
 * error channel.
 *
 * This map tracks what each dataset believes, NOT what the alert is showing:
 * `<EquitiesErrorAlert />` clears the source directly on dismiss, and the
 * record resets whenever the alert unmounts. Publishing is therefore
 * unconditional so a still-broken dataset re-asserts itself on the next
 * failure; only clearing is guarded, which is where the per-tick churn is.
 */
const activeErrors = new Map<MarketDataFunction, string>()

const syncErrorChannel = () => {
  if (activeErrors.size === 0) {
    clearEquitiesError("marketData")
    return
  }
  publishEquitiesError({
    source: "marketData",
    message: [...activeErrors.values()].join(" "),
  })
}

/**
 * @README
 * Symbols are deliberately left out of the copy: the live streams cover the
 * whole watchlist (an unreadable list), and a chart error is always about the
 * symbol already on screen. The dataset name carries the useful context.
 */
const publishMarketDataFnError = (
  dataset: MarketDataFunction,
  errorType: EquityErrorType,
) => {
  activeErrors.set(
    dataset,
    t("marketData.errors.failedToLoad", {
      dataset: t(`marketData.errors.functions.${dataset}`),
      message: t(equityErrorKey("marketData", errorType)),
    }),
  )
  syncErrorChannel()
}

// guarded: healthy streams emit per tick, and an unguarded clear would build a
// new error record — re-rendering every `useEquitiesErrors` subscriber — each time
const clearMarketDataFnError = (dataset: MarketDataFunction) => {
  if (!activeErrors.delete(dataset)) return

  syncErrorChannel()
}

// the raw failure stays in the console; users get generic translated copy
const onServerResponse = (
  fnType: MarketDataFunction,
  error: EquityError | null,
) => {
  if (!error) {
    clearMarketDataFnError(fnType)
    return
  }
  console.error("[marketData] Server-returned error:", error)
  publishMarketDataFnError(fnType, error.errorType)
}

const onStreamError = (fnType: MarketDataFunction, err: unknown) => {
  console.error("[marketData] Hydra stream error:", err)
  publishMarketDataFnError(fnType, EquityErrorType.InternalError)
}

export const getLatestBars$ = (
  input: LatestBarRequest,
): Observable<BarResponse> =>
  defer(() => MarketDataService.getLatestBars(input)).pipe(
    withConnection(),
    tap<LatestBarResult>({
      next: (response) =>
        onServerResponse(
          "latestBars",
          response.type === ERROR_LATEST_BAR_RESULT ? response.payload : null,
        ),
      error: (err) => onStreamError("latestBars", err),
    }),
    retry({ delay: 1_000 }),
    // one failing symbol must not tear down live prices for the rest of the watchlist
    filter(
      (response): response is SuccessLatestBarResult =>
        response.type !== ERROR_LATEST_BAR_RESULT,
    ),
    map((response) => response.payload),
  )

/**
 * @README - getHistoricalBars$
 * The BE fetches from Alpaca with Sort.DESC (newest-first) to guarantee
 * the most recent bars are included when the response is capped by `limit`.
 * We collect all emissions with reduce(), then reverse to chronological order
 * for the chart. withConnection() is placed after reduce() because reduce()
 * needs source completion to emit — if withConnection() were upstream, a
 * disconnect would complete the source with EMPTY, producing an empty array.
 */
export const getHistoricalBars$ = (
  input: HistoricalBarRequest,
): Observable<BarResponse[]> =>
  defer(() => MarketDataService.getHistoricalBars(input)).pipe(
    // an error frame can interleave with valid bars, so both are carried out of
    // reduce() and one decision is taken from the whole response.
    // The seed is created once per pipeline, not per subscription — keep the
    // accumulator immutable or bars leak between subscriptions.
    reduce<HistoricalBarResult, HistoricalBarsResponse>(
      (acc, curr) =>
        curr.type === ERROR_HISTORICAL_BAR_RESULT
          ? { ...acc, error: curr.payload }
          : { ...acc, bars: [...acc.bars, curr.payload] },
      { bars: [], error: null },
    ),
    withConnection(),
    tap<HistoricalBarsResponse>({
      next: ({ error }) => onServerResponse("historicalBars", error),
      error: (err) => onStreamError("historicalBars", err),
    }),
    retry({ delay: 1_000 }),
    map(({ bars }) => bars.toReversed()),
  )

export const getLatestQuote$ = (
  input: EquityQuoteRequest,
): Observable<EquityQuoteResponse> =>
  defer(() => MarketDataService.getLatestQuote(input)).pipe(
    withConnection(),
    tap<QuoteResult>({
      next: (response) =>
        onServerResponse(
          "latestQuote",
          response.type === ERROR_QUOTE_RESULT ? response.payload : null,
        ),
      error: (err) => onStreamError("latestQuote", err),
    }),
    retry({ delay: 1_000 }),
    // one failing symbol must not tear down live quotes for the rest of the watchlist
    filter(
      (response): response is SuccessQuoteResult =>
        response.type !== ERROR_QUOTE_RESULT,
    ),
    map((response) => response.payload),
  )
